import React, { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Trash2, Mail, ShieldCheck, Clock, Database, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";

/**
 * Public "Delete my account" page.
 *
 * WHY THIS EXISTS
 * ---------------
 * Google Play Store (as of 2024) REQUIRES every app that lets users create
 * accounts to publish a public webpage where anyone — including someone who
 * uninstalled the app or lost access to their device — can request that their
 * account and associated data be deleted.
 *
 * The page must:
 *   1) Refer to the app / developer name shown on the store listing.
 *   2) Prominently feature the steps a user takes to request deletion.
 *   3) Specify what data is deleted, what is kept, and any retention period.
 *
 * This page is served at `/delete-account` and is intentionally UNAUTHENTICATED
 * so a former user who has already lost account access can still find and use
 * it. It links out to the in-app self-serve deletion path AND provides a
 * fallback email path (hello@impoapp.com) for accounts that can't sign in.
 *
 * KEEP IN SYNC WITH:
 *   - /privacy   (Data we collect + Data retention sections)
 *   - /terms     (Termination section)
 */

const IN_APP_STEPS = [
  { label: "Open Impo on your device (or at https://impoapp.com)." },
  { label: "Sign in to the account you want to delete." },
  { label: 'Tap the "Profile" tab (bottom-right).' },
  { label: 'Scroll to "Data controls" (or "Account").' },
  { label: 'Tap "Delete my account".' },
  { label: "Confirm on the warning screen. You'll be signed out immediately." },
];

const EMAIL_STEPS = [
  {
    label: (
      <>
        Send an email to{" "}
        <a
          href="mailto:hello@impoapp.com?subject=Delete%20my%20Impo%20account"
          className="underline text-primary font-medium"
          data-testid="delete-account-mailto-link"
        >
          hello@impoapp.com
        </a>{" "}
        with the subject: <em>Delete my Impo account</em>.
      </>
    ),
  },
  {
    label: "Include the email address associated with your Impo account so we can locate it.",
  },
  {
    label:
      "You'll receive a confirmation email within 1 business day. Reply to confirm — this second step prevents someone else from deleting your account by impersonation.",
  },
  {
    label: "Your account and all associated data are permanently removed within 7 business days of confirmation.",
  },
];

const DATA_DELETED = [
  "Your Impo user record (name, email, phone, profile settings, plan)",
  "All Notes, Journal entries, Reminders, Documents, Scans, Photos, Videos, Voice Notes and other content you saved",
  "All Password vault entries (encrypted content is destroyed with your record — the encryption keys are also revoked)",
  "All Investments, Insurance, Medical and Travel records you created",
  "Your household membership and any families you own (co-members are notified; their own records are unaffected)",
  "All connected-service tokens (Google Calendar OAuth, WebAuthn passkeys, etc.)",
  "All analytics events tied to your pseudonymous ID (PostHog identifier is destroyed)",
];

const DATA_RETAINED = [
  {
    label: "Payment invoices",
    reason:
      "Retained by our payment processor (Razorpay) for up to 7 years to comply with GST and RBI record-keeping requirements applicable to Indian businesses. We only retain the transaction reference, not your account data.",
  },
  {
    label: "Aggregate/anonymised analytics",
    reason:
      "Counts like \"number of scans processed this month\" that cannot be tied back to any individual user are retained indefinitely for product and business reporting.",
  },
  {
    label: "Legal-hold data",
    reason:
      "If we are legally required to retain specific records (e.g. by a court order or regulator), we will preserve only those exact records for the duration required by law. You will be notified where legally permitted.",
  },
];

export default function DeleteAccount() {
  const nav = useNavigate();

  useEffect(() => {
    document.title = "Delete your account · Impo";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "How to permanently delete your Impo account and all associated data. Self-serve inside the app, or by emailing hello@impoapp.com."
      );
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="delete-account-page">
      {/* Header — echoes the /privacy + /terms header for consistency */}
      <header className="border-b border-border/60 bg-card/40 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2" data-testid="delete-account-logo-link">
            <Logo className="h-7 w-auto" />
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => nav(-1)}
            data-testid="delete-account-back-button"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 space-y-10">
        {/* Title block */}
        <section className="space-y-3">
          <div className="inline-flex items-center gap-2 rounded-full border border-destructive/20 bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive">
            <Trash2 className="h-3.5 w-3.5" />
            Account deletion
          </div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Delete your Impo account
          </h1>
          <p className="text-muted-foreground text-base leading-relaxed">
            Impo is a private, AI-powered memory vault. You own your data, and you can
            permanently delete your account — and everything stored under it — at any time.
            This page explains how, what gets removed, and what (if anything) is retained.
          </p>
          <p className="text-xs text-muted-foreground">
            Published by <strong>Impo — Personal Memory OS</strong>. Questions? Email{" "}
            <a href="mailto:hello@impoapp.com" className="underline">
              hello@impoapp.com
            </a>
            .
          </p>
        </section>

        {/* Option A — In-app self-serve */}
        <section
          className="rounded-2xl border border-border bg-card p-6 space-y-4"
          data-testid="delete-account-in-app-section"
        >
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Option A · Delete from inside the app (recommended)</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            The fastest way. Takes about 60 seconds. Deletion is immediate.
          </p>
          <ol className="space-y-2.5 list-decimal list-inside marker:text-primary marker:font-semibold text-sm leading-relaxed">
            {IN_APP_STEPS.map((s, i) => (
              <li key={i} className="pl-1">
                {s.label}
              </li>
            ))}
          </ol>
        </section>

        {/* Option B — Email us */}
        <section
          className="rounded-2xl border border-border bg-card p-6 space-y-4"
          data-testid="delete-account-email-section"
        >
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">
              Option B · Ask us by email (if you can't sign in)
            </h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Use this if you've lost access to your device or your login email is no longer
            reachable. We verify identity before deleting so no one can impersonate you.
          </p>
          <ol className="space-y-2.5 list-decimal list-inside marker:text-primary marker:font-semibold text-sm leading-relaxed">
            {EMAIL_STEPS.map((s, i) => (
              <li key={i} className="pl-1">
                {s.label}
              </li>
            ))}
          </ol>
        </section>

        {/* What is deleted */}
        <section className="space-y-4" data-testid="delete-account-deleted-section">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">What is permanently deleted</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Everything below is destroyed from our production database within 24 hours of
            your request being confirmed. Encrypted backups are purged within 30 days on
            their standard rotation.
          </p>
          <ul className="space-y-2 text-sm leading-relaxed">
            {DATA_DELETED.map((item, i) => (
              <li key={i} className="flex gap-2">
                <Trash2 className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* What is retained */}
        <section className="space-y-4" data-testid="delete-account-retained-section">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">What we may retain, and for how long</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            To comply with tax, accounting and legal requirements, a small number of
            records may survive account deletion. These are strictly limited to what the
            law requires and cannot be used to reconstruct your account.
          </p>
          <ul className="space-y-4 text-sm leading-relaxed">
            {DATA_RETAINED.map((item, i) => (
              <li key={i} className="rounded-lg border border-border/60 bg-card/40 p-4">
                <div className="font-medium">{item.label}</div>
                <div className="mt-1 text-muted-foreground">{item.reason}</div>
              </li>
            ))}
          </ul>
        </section>

        {/* Partial deletion */}
        <section
          className="rounded-2xl border border-border bg-card p-6 space-y-3"
          data-testid="delete-account-partial-section"
        >
          <h2 className="text-lg font-semibold">Just want to delete some data (not the whole account)?</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            You don't need to delete your account to remove specific data. Every note,
            reminder, document, photo, video, voice note, password entry and vertical
            record has its own <em>Delete</em> action inside the app (long-press on
            mobile, or tap the "…" menu on a card). Deletions there are permanent after a
            5-second "Undo" grace window.
          </p>
        </section>

        {/* Warning */}
        <section
          className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 space-y-2"
          data-testid="delete-account-warning-section"
        >
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <h2 className="text-lg font-semibold">Deletion is permanent and irreversible</h2>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Once your account is deleted, your data cannot be recovered — not by you, not
            by Impo support. If you want to keep a copy, first export your data from{" "}
            <strong>Profile → Data controls → Export</strong> (delivers a downloadable
            ZIP) before triggering deletion.
          </p>
        </section>

        {/* Contact + related links */}
        <section className="pt-6 border-t border-border/60 text-sm text-muted-foreground space-y-2">
          <p>
            <strong className="text-foreground">Need help?</strong>{" "}
            <a href="mailto:hello@impoapp.com" className="underline">
              hello@impoapp.com
            </a>{" "}
            · Response within 1 business day.
          </p>
          <p className="flex flex-wrap gap-x-4 gap-y-1">
            <Link to="/privacy" className="underline" data-testid="delete-account-privacy-link">
              Privacy policy
            </Link>
            <Link to="/terms" className="underline" data-testid="delete-account-terms-link">
              Terms of service
            </Link>
            <Link to="/" className="underline" data-testid="delete-account-home-link">
              Back to Impo
            </Link>
          </p>
          <p className="text-xs pt-2">Last updated: {new Date().toISOString().slice(0, 10)}</p>
        </section>
      </main>
    </div>
  );
}
